import * as zod from 'zod'
import type { DateFormat } from '../PersonalSettings'
import type { SortItem } from '../VuetifyFix'
import { COLLAPSABLE_TABLE, COMMON_WIDGET_DATA_CONSTRUCTOR, COMMON_WIDGET_ELEMENT_DATA, defaultInterval, IntervalLockSupportSchema, IntervalSupportSchema, WidgetElementSchema, WidgetSchema, type AbstractWidgetData, type BaseWidgetElementDialogProps, type IntervalSupport, type PartialWidget, type PartialWidgetElement, type WidgetConstructor, type WidgetElementData } from './Common'
import type { BaseWidgetDataRequest, WidgetDataResponse } from './Widgets'
import type { DataStatus, DepartmentPageLine } from '../Device'
import type { SetRequiredDeep } from 'type-fest'

export interface CollapsableTableHeaderData extends CollapsableTableHeader {
  sortable: boolean
  title: string
  headerSlot: `header.${string}`
  itemSlot: `item.${string}`
}
export type CollapsableTableCellType = 'NUMERIC' | 'DATE'
export interface DateCollapsableTableCellData extends CollapsableTableCell {
  text: string
  data: Date
  type: 'DATE'
}
export type MappedCollapsableTableCell = NumericCollapsableTableCell | DateCollapsableTableCellData
export type CollapsableTableRowData = Record<string, MappedCollapsableTableCell> & {
  timestamp: DateCollapsableTableCellData
}
export type CollapsableTableOptions = {
  rows: CollapsableTableRowData[]
  headers: CollapsableTableHeaderData[]
  sortItem: SortItem
  pageLength: number
  format: string
}
export type CollapsableTableWidgetDataRequest = BaseWidgetDataRequest & {
  type: 'COLLAPSABLE_TABLE'
  widget: CollapsableTableWidget
  dateFormat: DateFormat
}

export type CollapsableTableOptionsResponse = WidgetDataResponse & {
  type: 'COLLAPSABLE_TABLE'
  options: CollapsableTableOptions
}
const CollapseOnSchema = zod.union([
  zod.literal('ON_DAY'),
  zod.literal('ON_HOUR'),
  zod.literal('NONE'),
])
export type CollapseOn = zod.infer<typeof CollapseOnSchema>

export const CollapsableTableWidgetElementSchema = WidgetElementSchema.extend({
  type: COLLAPSABLE_TABLE,
  index: zod.number(),
  visible: zod.boolean(),
})
export type CollapsableTableWidgetElement = zod.infer<typeof CollapsableTableWidgetElementSchema>
export type CollapsableTableWidgetElementPartial = SetRequiredDeep<PartialWidgetElement<CollapsableTableWidgetElement>, 'type'>

export const CollapsableTableWidgetSchema = WidgetSchema.merge(IntervalSupportSchema).merge(IntervalLockSupportSchema).extend({
  type: COLLAPSABLE_TABLE,
  collapseOn: CollapseOnSchema,
  sortField: zod.string(),
  sortDesc: zod.boolean(),
  pageLength: zod.number(),
  showTimestamps: zod.boolean(),
  timestampsIndex: zod.number(),
  showUnits: zod.boolean(),
  elements: CollapsableTableWidgetElementSchema.array(),
})
export type CollapsableTableWidget = zod.infer<typeof CollapsableTableWidgetSchema>
export type CollapsableTableWidgetPartial = SetRequiredDeep<PartialWidget<CollapsableTableWidget>, 'type' | 'elements' | `elements.${number}.type`>

export interface CollapsableTableWidgetElementData extends WidgetElementData {
  index: number
  visible: boolean
}
export interface CollapsableTableHeader {
  text: string
  value: string
  index: number
  order: number
  visible: boolean
  status: DataStatus
}
export interface CollapsableTableCell {
  value: string
  unit: string
  type: CollapsableTableCellType
}
export interface NumericCollapsableTableCell extends CollapsableTableCell {
  text: number
  type: 'NUMERIC'
}
export interface StringCollapsableTableCell extends CollapsableTableCell {
  text: string
  type: 'DATE'
}
export type CollapsableTableRow = Record<string, MappedCollapsableTableCell> & {
  timestamp: StringCollapsableTableCell
}
export interface CollapsableTableWidgetData extends AbstractWidgetData<CollapsableTableWidgetElementData>, IntervalSupport {
  type: 'COLLAPSABLE_TABLE'
  elements: CollapsableTableWidgetElementData[]
  collapseOn: CollapseOn
  sortField: string
  sortDesc: boolean
  headers: CollapsableTableHeader[]
  rows: CollapsableTableRow[]
  pageLength: number
  showTimestamps: boolean
  timestampsIndex: number
  showUnits: boolean
}
export interface CollapsableTableWidgetElementDialogProps extends BaseWidgetElementDialogProps {
  type: 'COLLAPSABLE_TABLE'
  line: DepartmentPageLine
}
export const DEFAULT_COLLAPSABLE_TABLE_WIDGET_ELEMENT: CollapsableTableWidgetElementPartial = {
  ...COMMON_WIDGET_ELEMENT_DATA,
  visible: true,
  index: 0,
  type: 'COLLAPSABLE_TABLE',
}
export const COLLAPSABLE_TABLE_WIDGET_CONSTRUCTOR: WidgetConstructor<CollapsableTableWidgetPartial> = () => ({
  type: 'COLLAPSABLE_TABLE',
  ...COMMON_WIDGET_DATA_CONSTRUCTOR(),
  interval: defaultInterval(),
  collapseOn: 'NONE',
  showUnits: true,
  pageLength: 10,
  showTimestamps: true,
  timestampsIndex: -1,
  sortDesc: true,
  sortField: 'timestamp',
  elements: [DEFAULT_COLLAPSABLE_TABLE_WIDGET_ELEMENT],
})
